import { useEffect, useRef } from 'preact/hooks'
import { X, AlertTriangle } from 'lucide-preact'
import { fieldLimits } from '@/types/field-limits.generated'
import type { AgendaComparison } from '@/hooks/usePullAgenda'

const MAX_ITEM_LENGTH = fieldLimits.OnlineEvent.OnlineEventAgendaItem.MaxLength

interface PullAgendaModalProps {
  comparison: AgendaComparison
  overwriting: boolean
  onOverwrite: () => void
  onCancel: () => void
}

function AgendaColumn({ heading, items, flagTooLong }: { heading: string; items: string[]; flagTooLong?: boolean }) {
  return (
    <div className="min-w-0">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">{heading}</h4>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">No agenda items.</p>
      ) : (
        <ol className="space-y-2">
          {items.map((item, index) => {
            const tooLong = flagTooLong && item.length > MAX_ITEM_LENGTH
            return (
              <li key={index} className={`flex gap-2 rounded-md p-2 text-sm ${tooLong ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' : 'bg-slate-50 dark:bg-slate-900/40'}`}>
                <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-primary-600 text-white text-xs font-medium">{index + 1}</span>
                <span className="min-w-0 break-words whitespace-pre-line text-slate-700 dark:text-slate-300">
                  {item}
                  {tooLong && <span className="block mt-1 text-xs text-red-700 dark:text-red-400">{item.length.toLocaleString()} characters, over the {MAX_ITEM_LENGTH.toLocaleString()} limit</span>}
                </span>
              </li>
            )
          })}
        </ol>
      )}
    </div>
  )
}

/**
 * Shows the platform's agenda beside BigMarker's so the PM can choose to overwrite (ORB-943). There is
 * no merge. An item over the limit blocks Overwrite and is named, so nothing is cut short unseen.
 */
export function PullAgendaModal({ comparison, overwriting, onOverwrite, onCancel }: PullAgendaModalProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)

  // aria-modal only describes a dialog; it doesn't behave like one. Focus starts on Cancel (the safe
  // choice), Tab stays inside, Escape cancels, and focus goes back to the Pull button on close.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    cancelRef.current?.focus()
    return () => opener?.focus?.()
  }, [])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !overwriting) {
        event.preventDefault()
        onCancel()
        return
      }
      if (event.key !== 'Tab' || !panelRef.current) return
      const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'))
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [overwriting, onCancel])

  const tooLong = comparison.bigMarker
    .map((item, index) => ({ position: index + 1, length: item.length }))
    .filter(item => item.length > MAX_ITEM_LENGTH)

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="pull-agenda-title">
      <div className="fixed inset-0 bg-black/50" onClick={overwriting ? undefined : onCancel} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div ref={panelRef} className="relative w-full max-w-4xl rounded-lg bg-white dark:bg-slate-800 shadow-xl">
          <div className="flex items-start justify-between p-6 pb-4">
            <div>
              <h3 id="pull-agenda-title" className="text-lg font-semibold text-slate-900 dark:text-slate-100">Pull agenda from BigMarker</h3>
              <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                BigMarker&rsquo;s agenda is different. Overwrite replaces the agenda here with BigMarker&rsquo;s. Nothing is merged.
              </p>
            </div>
            <button type="button" onClick={onCancel} disabled={overwriting} aria-label="Close" className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-6 pb-6 max-h-[60vh] overflow-y-auto">
            {tooLong.length > 0 && (
              <div className="mb-4 flex items-start gap-2 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-800 dark:text-red-300">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  {tooLong.length === 1 ? `Item ${tooLong[0].position} is` : `Items ${tooLong.map(item => item.position).join(', ')} are`} longer than {MAX_ITEM_LENGTH.toLocaleString()} characters.
                  Shorten {tooLong.length === 1 ? 'it' : 'them'} in BigMarker, then pull again.
                </span>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <AgendaColumn heading="Here now" items={comparison.platform} />
              <AgendaColumn heading="In BigMarker" items={comparison.bigMarker} flagTooLong />
            </div>
          </div>

          <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900 rounded-b-lg flex justify-end gap-3">
            <button ref={cancelRef} type="button" onClick={onCancel} disabled={overwriting} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50">
              Cancel
            </button>
            <button type="button" onClick={onOverwrite} disabled={overwriting || tooLong.length > 0} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
              {overwriting ? 'Overwriting…' : 'Overwrite'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
